import { getClient } from '@api/client';
import { fragment } from '@api/fragment';
import { graphql } from '@api/generated';
import ServiceQueue from '../types/ServiceQueue';
import {
  serviceQueueFieldsFragment,
  serviceQueueStatsFragment,
} from './serviceQueueFragment';

const getServiceQueueByIdQuery = graphql(`
  query getServiceQueueByIdQuery($queueId: ID!, $skipStats: Boolean = true) {
    queue(queueId: $queueId) {
      ...ServiceQueueFields
      ...ServiceQueueStats @skip(if: $skipStats)
    }
  }
`);

/**
 * Fetch a single queue by id. Returns null when the queue does not exist.
 * Pass `fetchStats=true` to include the live status counters; defaults to
 * false to match the list query and avoid extra PCS calls on the BFF.
 */
async function getServiceQueueById(
  queueId: string,
  fetchStats = false,
): Promise<ServiceQueue | null> {
  const { data, error } = await getClient().query({
    query: getServiceQueueByIdQuery,
    fetchPolicy: 'network-only',
    variables: { queueId, skipStats: !fetchStats },
  });

  if (error) {
    throw new Error(error.message);
  }

  const queue = data?.queue;
  if (!queue) return null;

  const base = fragment(serviceQueueFieldsFragment, queue);
  const stats = fragment(serviceQueueStatsFragment, queue);
  return structuredClone({
    ...base,
    status: stats?.status ?? {},
  }) as ServiceQueue;
}

export default getServiceQueueById;
